/**
 * WB3-1 — Workbench focus model (intent · mode · variant · thread)
 * @see doc/system-completion-plan/01-target/BUSINESS_WORKBENCH_NORTH_STAR_V3.md §4 · §5
 */

import type { UserRole } from '@shared/types/roles';
import type { OperationalThread } from './operational-thread';
import {
  ROLE_INTENT_PRESETS,
  resolveNamespaceIntentPreset,
  type RoleIntentPreset,
  resolveRoleIntentPreset,
} from './intent-presets';
import {
  DEFAULT_OPERATIONAL_INTENT,
  type OperationalIntent,
  type WorkbenchIntent,
} from './workbench-intent';

export type {
  AccessIntent,
  DocumentIntent,
  IntentNamespace,
  OperationalIntent,
  RelationshipIntent,
  SiteIntent,
  WorkbenchIntent,
} from './workbench-intent';
export {
  applyIntentPriority,
  DEFAULT_OPERATIONAL_INTENT,
  filterActionsByIntent,
  getIntentNamespace,
  isAccessIntent,
  isDocumentIntent,
  isOperationalIntent,
  isRelationshipIntent,
  isSiteIntent,
  resolveIntentActionOrdering,
} from './workbench-intent';

export type WorkbenchVariant =
  | 'object'
  | 'flow'
  | 'task'
  | 'assignment'
  | 'site'
  | 'document'
  | 'conscious'
  | 'mobile_field';

/** variant === 'site' 일 때 primary sub-surface (§11-B) */
export type SiteSubSurface =
  | 'page_builder'
  | 'section_editor'
  | 'menu_binding'
  | 'action_binding'
  | 'portal_access'
  | 'publish_review';

export type WorkbenchMode =
  | 'view'
  | 'edit'
  | 'compare'
  | 'approve'
  | 'print_preview'
  | 'bulk_select'
  | 'investigate'
  | 'explain'
  | 'debug';

export interface ObjectRef {
  id: string;
  businessDomain: string;
  label?: string;
}

export type WorkbenchActionSurfaceKey =
  | 'primary'
  | 'secondary'
  | 'inspector'
  | 'contextMenu'
  | 'commandPalette'
  | 'bulk'
  | 'aiSuggested'
  | 'shortcuts';

export type { OperationalThread, OperationalThreadStep } from './operational-thread';

export interface ActorContext {
  tenantUserId: string;
  linkedEntityId: string;
  entityType: 'internal' | 'external';
  rolePreset?: string;
  permissions: readonly string[];
  teams?: string[];
  departments?: string[];
}

export interface WorkbenchFocus {
  root: ObjectRef;
  selection: ObjectRef[];
  intent: WorkbenchIntent;
  mode: WorkbenchMode;
  variant: WorkbenchVariant;
  /** variant === 'site' 일 때 primary sub-surface (§11-B) */
  siteSubSurface?: SiteSubSurface;
  thread?: OperationalThread;
  actionProjection?: WorkbenchActionSurfaceKey;
  /** @deprecated use actionProjection */
  surface?: WorkbenchActionSurfaceKey;
}

export const DEFAULT_WORKBENCH_MODE: WorkbenchMode = 'view';
export const DEFAULT_WORKBENCH_VARIANT: WorkbenchVariant = 'object';

const DOMAIN_INTENT_HINTS: Readonly<Record<string, OperationalIntent>> = {
  customer: 'process_new_order',
  external_entity: 'process_new_order',
  finance: 'collect_payment',
  finance_state: 'collect_payment',
  inventory: 'receive_inventory',
  item: 'receive_inventory',
  warehouse: 'receive_inventory',
};

const DOMAIN_NAMESPACE_INTENT_HINTS: Readonly<Record<string, WorkbenchIntent>> = {
  evidence_document: 'edit_template',
  org_document: 'preview_document',
  site: 'edit_site_page',
};

const PERMISSION_PRESET_HINTS: Readonly<Record<RoleIntentPreset, readonly (string | RegExp)[]>> = {
  finance: ['payment:', 'finance_state:', 'finance:'],
  sales: ['order:', 'sales:', 'quote:'],
  warehouse: ['inventory:', 'stock:', 'warehouse:', 'receive:'],
};

function matchesPermissionHint(key: string, hint: string | RegExp): boolean {
  if (typeof hint === 'string') {
    return key === hint || key.startsWith(hint);
  }
  return hint.test(key);
}

function inferPresetFromPermissions(
  permissionKeys: readonly string[] | undefined
): RoleIntentPreset | undefined {
  if (!permissionKeys || permissionKeys.length === 0) {
    return undefined;
  }

  for (const preset of ['finance', 'sales', 'warehouse'] as const) {
    const hints = PERMISSION_PRESET_HINTS[preset];
    const matched = permissionKeys.some((key) =>
      hints.some((hint) => matchesPermissionHint(key, hint))
    );
    if (matched) {
      return preset;
    }
  }

  return undefined;
}

function resolveDomainDefaultIntent(
  rootDomain: string,
  permissionKeys?: readonly string[]
): WorkbenchIntent | undefined {
  const normalized = rootDomain.trim().toLowerCase();
  if (normalized.length === 0) {
    return undefined;
  }

  const namespaceIntent = DOMAIN_NAMESPACE_INTENT_HINTS[normalized];
  if (namespaceIntent !== undefined) {
    return namespaceIntent;
  }

  for (const [domain, intent] of Object.entries(DOMAIN_NAMESPACE_INTENT_HINTS)) {
    if (normalized.includes(domain)) {
      return intent;
    }
  }

  const direct = DOMAIN_INTENT_HINTS[normalized];
  if (direct !== undefined) {
    return direct;
  }

  for (const [domain, intent] of Object.entries(DOMAIN_INTENT_HINTS)) {
    if (normalized.includes(domain)) {
      return intent;
    }
  }

  const permissionPreset = inferPresetFromPermissions(permissionKeys);
  if (permissionPreset !== undefined) {
    return ROLE_INTENT_PRESETS[permissionPreset];
  }

  return undefined;
}

function resolveRolePresetIntent(role: UserRole): OperationalIntent | undefined {
  const preset = resolveRoleIntentPreset(role);
  return preset !== undefined ? ROLE_INTENT_PRESETS[preset] : undefined;
}

/**
 * Resolves default workbench intent from role, root domain, variant, and JWT permission keys.
 */
export function resolveDefaultIntent(
  role: UserRole,
  rootDomain: string,
  permissionKeys?: readonly string[],
  variant: WorkbenchVariant = DEFAULT_WORKBENCH_VARIANT
): WorkbenchIntent {
  const variantPreset = resolveNamespaceIntentPreset(role, variant);
  if (variantPreset !== undefined) {
    return variantPreset;
  }

  const domainIntent = resolveDomainDefaultIntent(rootDomain, permissionKeys);
  if (domainIntent !== undefined) {
    return domainIntent;
  }

  const permissionPreset = inferPresetFromPermissions(permissionKeys);
  if (permissionPreset !== undefined) {
    return ROLE_INTENT_PRESETS[permissionPreset];
  }

  const roleNamespace = resolveNamespaceIntentPreset(role);
  if (roleNamespace !== undefined) {
    return roleNamespace;
  }

  const roleIntent = resolveRolePresetIntent(role);
  if (roleIntent !== undefined) {
    return roleIntent;
  }

  return DEFAULT_OPERATIONAL_INTENT;
}

export function createWorkbenchFocus(
  partial: Partial<WorkbenchFocus> & Pick<WorkbenchFocus, 'root'>
): WorkbenchFocus {
  const projection = partial.actionProjection ?? partial.surface ?? 'primary';
  return {
    selection: [],
    intent: DEFAULT_OPERATIONAL_INTENT,
    mode: DEFAULT_WORKBENCH_MODE,
    variant: DEFAULT_WORKBENCH_VARIANT,
    actionProjection: projection,
    surface: partial.surface ?? projection,
    ...partial,
  };
}

/** Intent/mode/variant are independent dimensions — patch one without resetting others. */
export function patchWorkbenchFocus(
  focus: WorkbenchFocus,
  patch: Partial<
    Pick<
      WorkbenchFocus,
      | 'intent'
      | 'mode'
      | 'variant'
      | 'siteSubSurface'
      | 'selection'
      | 'actionProjection'
      | 'surface'
      | 'thread'
    >
  >
): WorkbenchFocus {
  const next: WorkbenchFocus = { ...focus, ...patch };
  if (patch.actionProjection !== undefined) {
    next.surface = patch.actionProjection;
  } else if (patch.surface !== undefined) {
    next.actionProjection = patch.surface;
  }
  return next;
}
